/* eslint-disable @typescript-eslint/no-explicit-any */
// any is just used for a generic placeholder, Record<string, unknown> is used as a placeholder for props

import {
  ComponentType,
  createContext,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useState,
} from 'react';

type ModalContextType = {
  showModal: <P = Record<string, unknown>>(
    modal: ComponentType<P>,
    props: P,
  ) => void;
  hideModal: () => void;
};

export const ModalContext = createContext<ModalContextType>({
  hideModal: () => {},
  showModal: () => {},
});

type ModalProviderProps = {
  children: ReactNode;
};

type CurrentModalState<Props = Record<string, unknown>> = {
  modal: ComponentType<Props>;
  props: Props;
};

export function ModalProvider({ children }: ModalProviderProps): JSX.Element {
  const [currentModal, setCurrentModal] =
    useState<null | CurrentModalState<any>>(null);

  const showModal = useCallback(
    <P = Record<string, unknown>,>(modal: ComponentType<P>, props: P) => {
      setCurrentModal({ modal, props });
    },
    [],
  );

  const hideModal = useCallback(() => setCurrentModal(null), []);
  const [Modal, props] = currentModal
    ? [currentModal.modal, currentModal.props]
    : [];

  return (
    <ModalContext.Provider
      value={{
        showModal,
        hideModal,
      }}
    >
      {children}
      <Suspense fallback={null}>{Modal && <Modal {...props} />}</Suspense>
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextType {
  const { showModal, hideModal } = useContext<ModalContextType>(ModalContext);
  return {
    showModal,
    hideModal,
  };
}
